import { Component, Host, h, Listen, Element, Prop, Watch } from '@stencil/core';

export interface AhaImageCarousel extends HTMLAhaImageCarouselElement {}

/**
 * Custom image carousel component that implements `aha-carousel` and `aha-image-selector`.
 *
 * @slot carousel - Expects an `aha-carousel` component.
 * @slot selector - Expects an `aha-image-selector` component.
 */
@Component({
  tag: 'aha-image-carousel',
  styleUrl: 'aha-image-carousel.css',
  shadow: true,
})
export class AhaImageCarousel {
  @Element() host!: HTMLAhaImageCarouselElement;

  private ahaImageSelectorItems: string[] = [];
  /**
   * Selected index
   */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ reflect: true, mutable: true, attribute: 'selected-index' }) selectedIndex: number = 0;

  @Watch('selectedIndex')
  private watchSelectedIndex(nextIndex: number) {
    this.carouselSelectedIndex = nextIndex;
    this.imageSelectorSelectedIndex = nextIndex;
  }

  @Listen('aha-image-selector:active-index-change')
  listenSelectorChanges(event: CustomEvent<number>) {
    this.selectedIndex = event.detail;
  }

  @Listen('aha-carousel:active-index-change')
  listenCarouselChanges(event: CustomEvent<number>) {
    this.selectedIndex = event.detail;
  }

  @Listen('aha-image-selector:connected')
  listenImageSelectorConnected(event: CustomEvent<HTMLAhaImageSelectorElement>) {
    this.imageSelectorSelectedIndex = this.selectedIndex;
  }

  @Listen('aha-carousel:connected')
  listenCarouselConnected(event: CustomEvent<HTMLAhaCarouselElement>) {
    this.carouselSelectedIndex = this.selectedIndex;
  }

  @Listen('aha-image-selector-item:connected')
  listenImageSelectorItemConnected(event: CustomEvent<HTMLAhaImageSelectorItemElement>) {
    if (this.ahaImageSelectorItems.length > 1) return;
    this.ahaImageSelectorItems.push(event.detail.dataset.itemId as string);
    this.toggleImageSelector(this.ahaImageSelectorItems.length);
  }

  @Listen('aha-image-selector-item:disconnected')
  listenImageSelectorItemDisconnected(event: CustomEvent<HTMLAhaImageSelectorItemElement>) {
    const removeIndex = this.ahaImageSelectorItems.indexOf(event.detail.dataset.itemId as string);
    this.ahaImageSelectorItems.splice(removeIndex, 1);

    this.toggleImageSelector(this.ahaImageSelectorItems.length);
  }

  private get imageSelector() {
    return this.host.querySelector('aha-image-selector') as HTMLAhaImageSelectorElement;
  }

  private get carousel() {
    return this.host.querySelector('aha-carousel') as HTMLAhaCarouselElement;
  }

  private set imageSelectorSelectedIndex(index: number) {
    if (this.imageSelector) this.imageSelector.selectedIndex = index;
  }

  private set carouselSelectedIndex(index: number) {
    if (this.carousel) this.carousel.selectedIndex = index;
  }

  private toggleImageSelector(itemsLength: number) {
    if (itemsLength === 1) {
      this.imageSelector.setAttribute('aria-hidden', 'true');
    } else if (itemsLength > 1) {
      this.imageSelector.setAttribute('aria-hidden', 'false');
    }
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
